import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUsersList, createUser, updateUser, deleteUser, getRoles, getSekolahList, getInstansiList } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  Users, UserPlus, Edit2, Trash2, Search, Loader2, 
  RefreshCw, Eye, EyeOff, Shield, Building2, ChevronDown, Landmark
} from 'lucide-react';
import { toast } from '../ui/sonner';

const ROLE_COLORS = {
  admin_dinas: 'bg-purple-100 text-purple-800',
  pimpinan: 'bg-indigo-100 text-indigo-800',
  editor: 'bg-blue-100 text-blue-800',
  pengawas: 'bg-cyan-100 text-cyan-800',
  kepala_sekolah: 'bg-amber-100 text-amber-800',
  guru: 'bg-emerald-100 text-emerald-800',
  tenaga_tata_usaha: 'bg-teal-100 text-teal-800'
};

const ROLE_LABELS = {
  admin_dinas: 'Admin Dinas',
  pimpinan: 'Pimpinan',
  editor: 'Editor',
  pengawas: 'Pengawas',
  kepala_sekolah: 'Kepala Sekolah',
  guru: 'Guru',
  tenaga_tata_usaha: 'Tenaga Tata Usaha'
};

// Roles that editor can manage
const EDITOR_ALLOWED_ROLES = ['guru', 'kepala_sekolah', 'tenaga_tata_usaha'];

// Status Kepegawaian options for Guru/Kepala Sekolah
const STATUS_KEPEGAWAIAN_OPTIONS = [
  'ASN',
  'PPPK',
  'Guru Kontrak',
  'Guru Honor Daerah',
  'Non-ASN',
  'Guru Kontrak Luar Papua'
];

const STATUS_KEPEGAWAIAN_COLORS = {
  'ASN': 'bg-green-100 text-green-800',
  'PPPK': 'bg-blue-100 text-blue-800',
  'Guru Kontrak': 'bg-yellow-100 text-yellow-800',
  'Guru Honor Daerah': 'bg-orange-100 text-orange-800',
  'Non-ASN': 'bg-gray-100 text-gray-800',
  'Guru Kontrak Luar Papua': 'bg-purple-100 text-purple-800'
};

const UserManagementPage = () => {
  const { user: currentUser, isEditor } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  const [instansiList, setInstansiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterSekolah, setFilterSekolah] = useState('all');
  const [viewMode, setViewMode] = useState('grouped'); // 'list' or 'grouped'
  const [expandedSchools, setExpandedSchools] = useState(new Set()); // Track expanded school cards
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nip: '',
    nama: '',
    email: '',
    role: 'guru',
    sekolah_id: '',
    sekolah_ids: [],
    instansi_id: '',
    password: '',
    status_kepegawaian: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users and sekolah list - available to all who can access this page
      const [usersData, sekolah] = await Promise.all([
        getUsersList(),
        getSekolahList()
      ]);
      setUsers(usersData);
      setSekolahList(sekolah);
      
      // Load roles and instansi only for admin_dinas and pimpinan
      // Editor doesn't need these - they use hardcoded EDITOR_ALLOWED_ROLES
      if (!isEditor) {
        try {
          const [rolesData, instansi] = await Promise.all([
            getRoles(),
            getInstansiList()
          ]);
          setRoles(rolesData);
          setInstansiList(instansi || []);
        } catch (err) {
          console.log('Roles/Instansi load skipped for this role');
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nip: '',
      nama: '',
      email: '',
      role: 'guru',
      sekolah_id: '',
      sekolah_ids: [],
      instansi_id: '',
      password: '',
      status_kepegawaian: ''
    });
    setShowPassword(false);
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      nip: user.nip,
      nama: user.nama,
      email: user.email || '',
      role: user.role,
      sekolah_id: user.sekolah_id || '',
      sekolah_ids: user.sekolah_ids || [],
      instansi_id: user.instansi_id || '',
      password: '',
      status_kepegawaian: user.status_kepegawaian || ''
    });
    setShowEditDialog(true);
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSekolahIdsChange = (sekolahId, checked) => {
    setFormData(prev => ({
      ...prev,
      sekolah_ids: checked 
        ? [...prev.sekolah_ids, sekolahId]
        : prev.sekolah_ids.filter(id => id !== sekolahId)
    }));
  };

  const handleCreateUser = async () => {
    try {
      setSubmitting(true);
      
      // Validation
      if (!formData.nip || !formData.nama || !formData.password) {
        toast.error('NIP, Nama, dan Password wajib diisi');
        return;
      }
      
      if (formData.role === 'guru' || formData.role === 'kepala_sekolah' || formData.role === 'tenaga_tata_usaha') {
        if (!formData.sekolah_id) {
          toast.error('Sekolah wajib dipilih untuk role ini');
          return;
        }
      }
      
      const payload = {
        nip: formData.nip,
        nama: formData.nama,
        email: formData.email || null,
        role: formData.role,
        sekolah_id: formData.sekolah_id || null,
        sekolah_ids: formData.role === 'pengawas' ? formData.sekolah_ids : null,
        instansi_id: ['pimpinan', 'editor', 'pegawai_instansi'].includes(formData.role) ? (formData.instansi_id || null) : null,
        password: formData.password,
        status_kepegawaian: ['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) ? (formData.status_kepegawaian || null) : null
      };
      
      await createUser(payload);
      toast.success('User berhasil ditambahkan');
      setShowAddDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Gagal menambahkan user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setSubmitting(true);
      
      if (!formData.nama) {
        toast.error('Nama wajib diisi');
        return;
      }
      
      const payload = {
        nama: formData.nama,
        email: formData.email || null,
        role: formData.role,
        sekolah_id: formData.sekolah_id || null,
        sekolah_ids: formData.role === 'pengawas' ? formData.sekolah_ids : null,
        instansi_id: ['pimpinan', 'editor', 'pegawai_instansi'].includes(formData.role) ? (formData.instansi_id || null) : null,
        status_kepegawaian: ['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) ? (formData.status_kepegawaian || null) : null
      };
      
      if (formData.password) {
        payload.password = formData.password;
      }
      
      await updateUser(selectedUser.id, payload);
      toast.success('User berhasil diperbarui');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Gagal memperbarui user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setSubmitting(true);
      await deleteUser(selectedUser.id);
      toast.success('User berhasil dihapus');
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchSearch = searchQuery === '' || 
      user.nama.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.nip.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchRole = filterRole === 'all' || user.role === filterRole;
    const matchSekolah = filterSekolah === 'all' || user.sekolah_id === filterSekolah;
    
    return matchSearch && matchRole && matchSekolah;
  });

  const getSekolahName = (sekolahId) => {
    const sekolah = sekolahList.find(s => s.id === sekolahId);
    return sekolah ? sekolah.nama : '-';
  };

  // Group users by school for grouped view
  const groupedUsers = sekolahList.map(sekolah => {
    const schoolUsers = filteredUsers.filter(u => u.sekolah_id === sekolah.id);
    return {
      sekolah,
      users: schoolUsers,
      kepalaSekolah: schoolUsers.filter(u => u.role === 'kepala_sekolah'),
      guru: schoolUsers.filter(u => u.role === 'guru'),
      tataUsaha: schoolUsers.filter(u => u.role === 'tenaga_tata_usaha')
    };
  }).filter(group => group.users.length > 0);

  // Users without school
  const usersWithoutSchool = filteredUsers.filter(u => !u.sekolah_id);

  // Toggle school expansion
  const toggleSchoolExpand = (schoolId) => {
    const newExpanded = new Set(expandedSchools);
    if (newExpanded.has(schoolId)) {
      newExpanded.delete(schoolId);
    } else {
      newExpanded.add(schoolId);
    }
    setExpandedSchools(newExpanded);
  };

  // Expand all schools
  const expandAllSchools = () => {
    setExpandedSchools(new Set(groupedUsers.map(g => g.sekolah.id)));
  };

  // Collapse all schools
  const collapseAllSchools = () => {
    setExpandedSchools(new Set());
  };

  // Check if current user can edit (admin_dinas or editor)
  const canEdit = currentUser?.role === 'admin_dinas' || isEditor;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">Manajemen User</h1>
          <p className="text-gray-500">Kelola semua pengguna sistem</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadData} className="border-indigo-200">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canEdit && (
            <Button 
              onClick={handleOpenAddDialog}
              className="bg-indigo-950 hover:bg-indigo-900"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Tambah User
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-indigo-100/50 mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cari berdasarkan nama, NIP, atau email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger data-testid="filter-role">
                <SelectValue placeholder="Filter Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Role</SelectItem>
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSekolah} onValueChange={setFilterSekolah}>
              <SelectTrigger data-testid="filter-sekolah">
                <SelectValue placeholder="Filter Sekolah" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Sekolah</SelectItem>
                {sekolahList.map(sekolah => (
                  <SelectItem key={sekolah.id} value={sekolah.id}>{sekolah.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grouped' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grouped')}
                className={viewMode === 'grouped' ? 'bg-indigo-950' : ''}
                data-testid="view-grouped-btn"
              >
                <Building2 className="w-4 h-4 mr-1" />
                Per Sekolah
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-indigo-950' : ''}
                data-testid="view-list-btn"
              >
                <Users className="w-4 h-4 mr-1" />
                Daftar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {Object.entries(ROLE_LABELS).map(([role, label]) => {
          const count = users.filter(u => u.role === role).length;
          return (
            <Card key={role} className="border-indigo-100/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-indigo-950">{count}</p>
                <p className="text-sm text-gray-500">{label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Grouped View */}
      {viewMode === 'grouped' && (
        <>
          {/* Expand/Collapse Controls */}
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={expandAllSchools}>
              Buka Semua
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAllSchools}>
              Tutup Semua
            </Button>
          </div>
          
          {/* Schools */}
          <div className="space-y-4">
            {groupedUsers.map(({ sekolah, users: schoolUsers, kepalaSekolah, guru, tataUsaha }) => (
              <Card key={sekolah.id} className="border-indigo-100/50">
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSchoolExpand(sekolah.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-indigo-600" />
                      <div>
                        <CardTitle className="text-lg">{sekolah.nama}</CardTitle>
                        <p className="text-sm text-gray-500">
                          {kepalaSekolah.length} Kepala Sekolah • {guru.length} Guru • {tataUsaha.length} Tata Usaha
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-indigo-100 text-indigo-800">
                        {schoolUsers.length} pengguna
                      </Badge>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedSchools.has(sekolah.id) ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                </CardHeader>
                
                {expandedSchools.has(sekolah.id) && (
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>NIP</th>
                            <th>Nama</th>
                            <th>Role</th>
                            <th>Status Kepegawaian</th>
                            {canEdit && <th>Aksi</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {schoolUsers.map(user => (
                            <tr key={user.id}>
                              <td className="font-mono text-sm">{user.nip}</td>
                              <td className="font-medium">{user.nama}</td>
                              <td>
                                <Badge className={ROLE_COLORS[user.role]}>
                                  {ROLE_LABELS[user.role]}
                                </Badge>
                              </td>
                              <td>
                                {user.status_kepegawaian ? (
                                  <Badge className={STATUS_KEPEGAWAIAN_COLORS[user.status_kepegawaian] || 'bg-gray-100 text-gray-800'}>
                                    {user.status_kepegawaian}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              {canEdit && (
                                <td>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(user); }}
                                    >
                                      <Edit2 className="w-4 h-4 text-indigo-600" />
                                    </Button>
                                    {user.id !== currentUser?.id && !isEditor && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(user); }}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
            
            {/* Users without school (admin, pimpinan, etc) */}
            {usersWithoutSchool.length > 0 && (
              <Card className="border-gray-200">
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSchoolExpand('no-school')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-gray-600" />
                      <div>
                        <CardTitle className="text-lg">Pengguna Tanpa Sekolah</CardTitle>
                        <p className="text-sm text-gray-500">Admin Dinas, Pimpinan, Editor, Pengawas</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-gray-100 text-gray-800">
                        {usersWithoutSchool.length} pengguna
                      </Badge>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedSchools.has('no-school') ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                </CardHeader>
                
                {expandedSchools.has('no-school') && (
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>NIP</th>
                            <th>Nama</th>
                            <th>Email</th>
                            <th>Role</th>
                            {canEdit && <th>Aksi</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {usersWithoutSchool.map(user => (
                            <tr key={user.id}>
                              <td className="font-mono text-sm">{user.nip}</td>
                              <td className="font-medium">{user.nama}</td>
                              <td className="text-gray-500">{user.email || '-'}</td>
                              <td>
                                <Badge className={ROLE_COLORS[user.role]}>
                                  {ROLE_LABELS[user.role]}
                                </Badge>
                              </td>
                              {canEdit && (
                                <td>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(user); }}
                                    >
                                      <Edit2 className="w-4 h-4 text-indigo-600" />
                                    </Button>
                                    {user.id !== currentUser?.id && !isEditor && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(user); }}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
            
            {groupedUsers.length === 0 && usersWithoutSchool.length === 0 && (
              <Card className="border-indigo-100/50">
                <CardContent className="py-12 text-center text-gray-500">
                  Tidak ada pengguna yang ditemukan
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* List View - Original Table */}
      {viewMode === 'list' && (
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Daftar User ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>NIP</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status Kepegawaian</th>
                  <th>Sekolah</th>
                  {canEdit && <th>Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="font-mono text-sm">{user.nip}</td>
                    <td className="font-medium">{user.nama}</td>
                    <td className="text-gray-500">{user.email || '-'}</td>
                    <td>
                      <Badge className={ROLE_COLORS[user.role]}>
                        {ROLE_LABELS[user.role]}
                      </Badge>
                    </td>
                    <td>
                    {['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(user.role) && user.status_kepegawaian ? (
                        <Badge className={STATUS_KEPEGAWAIAN_COLORS[user.status_kepegawaian] || 'bg-gray-100 text-gray-800'}>
                          {user.status_kepegawaian}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      {user.role === 'pengawas' && user.sekolah_ids?.length > 0 ? (
                        <span className="text-sm">
                          {user.sekolah_ids.length} sekolah
                        </span>
                      ) : (
                        getSekolahName(user.sekolah_id)
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEditDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-indigo-600" />
                          </Button>
                          {user.id !== currentUser?.id && !isEditor && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDeleteDialog(user)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 7 : 6} className="text-center text-gray-500 py-8">
                      Tidak ada user yang ditemukan
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Tambah User Baru
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nip">NIP/NUPTK *</Label>
              <Input
                id="nip"
                value={formData.nip}
                onChange={(e) => handleInputChange('nip', e.target.value)}
                placeholder="Masukkan NIP/NUPTK"
                data-testid="input-nip"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nama">Nama Lengkap *</Label>
              <Input
                id="nama"
                value={formData.nama}
                onChange={(e) => handleInputChange('nama', e.target.value)}
                placeholder="Masukkan nama lengkap"
                data-testid="input-nama"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Masukkan email"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
                <SelectTrigger data-testid="select-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS)
                    .filter(([value]) => !isEditor || EDITOR_ALLOWED_ROLES.includes(value))
                    .map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Show sekolah for guru, kepala_sekolah, tenaga_tata_usaha, editor */}
            {['guru', 'kepala_sekolah', 'tenaga_tata_usaha', 'editor'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="sekolah">Sekolah {['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) ? '*' : ''}</Label>
                <Select value={formData.sekolah_id} onValueChange={(v) => handleInputChange('sekolah_id', v)}>
                  <SelectTrigger data-testid="select-sekolah">
                    <SelectValue placeholder="Pilih Sekolah" />
                  </SelectTrigger>
                  <SelectContent>
                    {sekolahList.map(sekolah => (
                      <SelectItem key={sekolah.id} value={sekolah.id}>{sekolah.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show instansi for pimpinan, editor, pegawai_instansi */}
            {['pimpinan', 'editor', 'pegawai_instansi'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="instansi">Instansi {formData.role === 'pegawai_instansi' ? '*' : ''}</Label>
                <Select value={formData.instansi_id} onValueChange={(v) => handleInputChange('instansi_id', v)}>
                  <SelectTrigger data-testid="select-instansi">
                    <SelectValue placeholder="Pilih Instansi" />
                  </SelectTrigger>
                  <SelectContent>
                    {instansiList.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status Kepegawaian for guru/kepala_sekolah/tenaga_tata_usaha */}
            {['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="status_kepegawaian">Status Kepegawaian</Label>
                <Select value={formData.status_kepegawaian} onValueChange={(v) => handleInputChange('status_kepegawaian', v)}>
                  <SelectTrigger data-testid="select-status-kepegawaian">
                    <SelectValue placeholder="Pilih Status Kepegawaian" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_KEPEGAWAIAN_OPTIONS.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Show multiple sekolah for pengawas */}
            {formData.role === 'pengawas' && (
              <div className="space-y-2">
                <Label>Sekolah yang Diawasi</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {sekolahList.map(sekolah => (
                    <label key={sekolah.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sekolah_ids.includes(sekolah.id)}
                        onChange={(e) => handleSekolahIdsChange(sekolah.id, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{sekolah.nama}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Masukkan password"
                  data-testid="input-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={submitting}
              className="bg-indigo-950 hover:bg-indigo-900"
              data-testid="submit-add-user"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Edit User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nip">NIP/NUPTK</Label>
              <Input
                id="edit-nip"
                value={formData.nip}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-nama">Nama Lengkap *</Label>
              <Input
                id="edit-nama"
                value={formData.nama}
                onChange={(e) => handleInputChange('nama', e.target.value)}
                placeholder="Masukkan nama lengkap"
                data-testid="edit-input-nama"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Masukkan email"
                data-testid="edit-input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
                <SelectTrigger data-testid="edit-select-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS)
                    .filter(([value]) => !isEditor || EDITOR_ALLOWED_ROLES.includes(value))
                    .map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {['guru', 'kepala_sekolah', 'tenaga_tata_usaha', 'editor'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="edit-sekolah">Sekolah {['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) ? '*' : ''}</Label>
                <Select value={formData.sekolah_id} onValueChange={(v) => handleInputChange('sekolah_id', v)}>
                  <SelectTrigger data-testid="edit-select-sekolah">
                    <SelectValue placeholder="Pilih Sekolah" />
                  </SelectTrigger>
                  <SelectContent>
                    {sekolahList.map(sekolah => (
                      <SelectItem key={sekolah.id} value={sekolah.id}>{sekolah.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show instansi for pimpinan, editor, pegawai_instansi */}
            {['pimpinan', 'editor', 'pegawai_instansi'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="edit-instansi">Instansi {formData.role === 'pegawai_instansi' ? '*' : ''}</Label>
                <Select value={formData.instansi_id} onValueChange={(v) => handleInputChange('instansi_id', v)}>
                  <SelectTrigger data-testid="edit-select-instansi">
                    <SelectValue placeholder="Pilih Instansi" />
                  </SelectTrigger>
                  <SelectContent>
                    {instansiList.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status Kepegawaian for guru/kepala_sekolah/tenaga_tata_usaha */}
            {['guru', 'kepala_sekolah', 'tenaga_tata_usaha'].includes(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="edit-status-kepegawaian">Status Kepegawaian</Label>
                <Select value={formData.status_kepegawaian} onValueChange={(v) => handleInputChange('status_kepegawaian', v)}>
                  <SelectTrigger data-testid="edit-select-status-kepegawaian">
                    <SelectValue placeholder="Pilih Status Kepegawaian" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_KEPEGAWAIAN_OPTIONS.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {formData.role === 'pengawas' && (
              <div className="space-y-2">
                <Label>Sekolah yang Diawasi</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {sekolahList.map(sekolah => (
                    <label key={sekolah.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sekolah_ids.includes(sekolah.id)}
                        onChange={(e) => handleSekolahIdsChange(sekolah.id, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{sekolah.nama}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="edit-password">Password Baru (Kosongkan jika tidak ingin mengubah)</Label>
              <div className="relative">
                <Input
                  id="edit-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Masukkan password baru"
                  data-testid="edit-input-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleUpdateUser} 
              disabled={submitting}
              className="bg-indigo-950 hover:bg-indigo-900"
              data-testid="submit-edit-user"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Hapus User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Apakah Anda yakin ingin menghapus user <strong>{selectedUser?.nama}</strong>?
            </p>
            <p className="text-sm text-red-500 mt-2">
              Tindakan ini tidak dapat dibatalkan.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              disabled={submitting}
              variant="destructive"
              data-testid="confirm-delete-user"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
